import React from "react";
import { Box, Container, Heading, Text, VStack, SimpleGrid, Button, Icon, List, ListItem, ListIcon, HStack, Badge } from "@chakra-ui/react";
import { FaSolarPanel, FaHome, FaCheckCircle, FaArrowRight, FaChartLine, FaUsers, FaLeaf } from "react-icons/fa";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";
import { useNavigate } from "react-router-dom";

const SolutionsSolarProperty: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/solutions/solar-property"
        fallbackTitle="Solar + Property Solutions"
        fallbackDescription="Maximize returns by combining solar installations with strategic property investments"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={brandConfig.colors.primary} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Badge bg="#C2A878" color={brandConfig.colors.primary} fontSize="sm" px={4} py={2} borderRadius="full">
              Solar + Property
            </Badge>
            <Heading size="2xl" fontWeight="bold">
              Solar + Property Investment
            </Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="2xl">
              Add solar to investment properties and increase value while attracting quality tenants
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Problem Section */}
      <Container maxW="container.xl" py={20}>
        <VStack spacing={8} mb={16}>
          <Heading size="xl" textAlign="center" color={brandConfig.colors.primary}>
            Why Combine Solar + Property?
          </Heading>
          <Text fontSize="lg" textAlign="center" maxW="3xl" color="gray.600">
            Investment properties with solar panels command higher rents, attract better tenants, and increase in value faster than non-solar properties. It's a winning combination for savvy investors.
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
          <Box p={6} bg="gray.50" borderRadius="lg">
            <Icon as={FaChartLine} w={10} h={10} color={brandConfig.colors.primary} mb={4} />
            <Heading size="sm" mb={3} color={brandConfig.colors.primary}>
              Higher Property Value
            </Heading>
            <Text color="gray.600" fontSize="sm">
              Properties with solar systems sell for 4-6% more on average and spend less time on the market
            </Text>
          </Box>

          <Box p={6} bg="gray.50" borderRadius="lg">
            <Icon as={FaUsers} w={10} h={10} color={brandConfig.colors.primary} mb={4} />
            <Heading size="sm" mb={3} color={brandConfig.colors.primary}>
              Better Tenants
            </Heading>
            <Text color="gray.600" fontSize="sm">
              Solar properties attract environmentally conscious, long-term tenants who take better care of your investment
            </Text>
          </Box>

          <Box p={6} bg="gray.50" borderRadius="lg">
            <Icon as={FaLeaf} w={10} h={10} color={brandConfig.colors.primary} mb={4} />
            <Heading size="sm" mb={3} color={brandConfig.colors.primary}>
              Competitive Advantage
            </Heading>
            <Text color="gray.600" fontSize="sm">
              Stand out from other properties with lower energy bills and sustainable features
            </Text>
          </Box>
        </SimpleGrid>
      </Container>

      {/* Solution Benefits Section */}
      <Box bg="gray.50" py={20}>
        <Container maxW="container.xl">
          <Heading size="xl" textAlign="center" mb={12} color={brandConfig.colors.primary}>
            Key Benefits
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10}>
            <Box p={8} bg="white" borderRadius="lg" boxShadow="md">
              <Heading size="md" mb={4} color={brandConfig.colors.primary}>
                Increased Rental Income
              </Heading>
              <Text color="gray.600" mb={4}>
                Charge premium rents for solar-equipped properties. Tenants are willing to pay 5-10% more for homes with solar because of lower energy bills.
              </Text>
              <List spacing={3}>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                  $20-50/week higher rent
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                  Lower vacancy rates
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                  Longer tenancy periods
                </ListItem>
              </List>
            </Box>

            <Box p={8} bg="white" borderRadius="lg" boxShadow="md">
              <Heading size="md" mb={4} color={brandConfig.colors.primary}>
                Tax Benefits
              </Heading>
              <Text color="gray.600" mb={4}>
                Claim solar installation costs as a tax deduction through depreciation schedules and immediate write-offs for small business investors.
              </Text>
              <List spacing={3}>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                  Instant asset write-off eligible
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                  Depreciation deductions
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                  Improve cash flow
                </ListItem>
              </List>
            </Box>

            <Box p={8} bg="white" borderRadius="lg" boxShadow="md">
              <Heading size="md" mb={4} color={brandConfig.colors.primary}>
                Property Appreciation
              </Heading>
              <Text color="gray.600" mb={4}>
                Solar-equipped properties appreciate faster and attract more buyers when it's time to sell. Future-proof your investment.
              </Text>
              <List spacing={3}>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                  4-6% higher sale prices
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                  Faster selling times
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                  More buyer interest
                </ListItem>
              </List>
            </Box>

            <Box p={8} bg="white" borderRadius="lg" boxShadow="md">
              <Heading size="md" mb={4} color={brandConfig.colors.primary}>
                Reduced Maintenance Issues
              </Heading>
              <Text color="gray.600" mb={4}>
                Modern solar systems require minimal maintenance. With 25-year warranties, you're protected from unexpected costs.
              </Text>
              <List spacing={3}>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                  25-year panel warranty
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                  10-year inverter warranty
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                  Monitoring included
                </ListItem>
              </List>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Container maxW="container.xl" py={20}>
        <Heading size="xl" textAlign="center" mb={12} color={brandConfig.colors.primary}>
          How It Works
        </Heading>
        <VStack spacing={8} align="stretch">
          <HStack
            p={8}
            bg="white"
            borderRadius="lg"
            boxShadow="md"
            spacing={6}
            align="start"
            flexDirection={{ base: "column", md: "row" }}
          >
            <Box
              bg={brandConfig.colors.primary}
              color="white"
              borderRadius="full"
              w="60px"
              h="60px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="2xl"
              fontWeight="bold"
              flexShrink={0}
            >
              1
            </Box>
            <Box>
              <Heading size="md" mb={2} color={brandConfig.colors.primary}>
                Property Assessment
              </Heading>
              <Text color="gray.600">
                We assess your investment property to determine the optimal solar system size and configuration. Our team evaluates roof condition, orientation, and energy usage patterns.
              </Text>
            </Box>
          </HStack>

          <HStack
            p={8}
            bg="white"
            borderRadius="lg"
            boxShadow="md"
            spacing={6}
            align="start"
            flexDirection={{ base: "column", md: "row" }}
          >
            <Box
              bg={brandConfig.colors.primary}
              color="white"
              borderRadius="full"
              w="60px"
              h="60px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="2xl"
              fontWeight="bold"
              flexShrink={0}
            >
              2
            </Box>
            <Box>
              <Heading size="md" mb={2} color={brandConfig.colors.primary}>
                Financial Structuring
              </Heading>
              <Text color="gray.600">
                Our finance team structures the solar investment to maximize tax benefits and ensure positive cash flow. We can bundle solar costs into your property loan or arrange separate financing.
              </Text>
            </Box>
          </HStack>

          <HStack
            p={8}
            bg="white"
            borderRadius="lg"
            boxShadow="md"
            spacing={6}
            align="start"
            flexDirection={{ base: "column", md: "row" }}
          >
            <Box
              bg={brandConfig.colors.primary}
              color="white"
              borderRadius="full"
              w="60px"
              h="60px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="2xl"
              fontWeight="bold"
              flexShrink={0}
            >
              3
            </Box>
            <Box>
              <Heading size="md" mb={2} color={brandConfig.colors.primary}>
                Installation & Approval
              </Heading>
              <Text color="gray.600">
                Professional installation by certified technicians with all council approvals and grid connection handled. Typically completed within 2-4 weeks from approval.
              </Text>
            </Box>
          </HStack>

          <HStack
            p={8}
            bg="white"
            borderRadius="lg"
            boxShadow="md"
            spacing={6}
            align="start"
            flexDirection={{ base: "column", md: "row" }}
          >
            <Box
              bg={brandConfig.colors.primary}
              color="white"
              borderRadius="full"
              w="60px"
              h="60px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="2xl"
              fontWeight="bold"
              flexShrink={0}
            >
              4
            </Box>
            <Box>
              <Heading size="md" mb={2} color={brandConfig.colors.primary}>
                Tenant & Ongoing Management
              </Heading>
              <Text color="gray.600">
                Market the property's solar benefits to attract quality tenants. We provide monitoring and maintenance support to keep your system performing optimally for decades.
              </Text>
            </Box>
          </HStack>
        </VStack>
      </Container>

      {/* CTA Section */}
      <Box bg={brandConfig.colors.primary} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Heading size="xl">
              Ready to Add Solar to Your Investment Property?
            </Heading>
            <Text fontSize="lg" maxW="2xl" color="rgba(255, 255, 255, 0.9)">
              Get a free property assessment and discover how much you can boost your rental income and property value with solar.
            </Text>
            <Button
              size="lg"
              bg="#C2A878"
              color={brandConfig.colors.primary}
              _hover={{ bg: "#b09060", transform: "translateY(-2px)" }}
              rightIcon={<Icon as={FaArrowRight} />}
              onClick={() => navigate("/contact")}
              transition="all 0.2s"
              fontWeight="bold"
            >
              Get Free Property Assessment
            </Button>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default SolutionsSolarProperty;
